/**
 * Single-card dashboard tile — the three previously-stacked tiles
 * (aggregate CEFR position, per-band breakdown, vocabulary summary)
 * fused into one. Bars stay the visual centre of gravity; the other
 * numbers ride along in the header strip.
 *
 * Used on /home. The standalone CefrProgressCard + CefrBandBreakdown
 * pair still lives on /progress where each gets its own dedicated
 * section. VocabularyCard is gone from /home (its data is now here).
 *
 * Pulls /stats/cefr-progress for the aggregate (React Query shares
 * the cache key with the standalone CefrProgressCard, so the network
 * round-trip is shared) and walks the skills prop for the band stats.
 */

import { useQuery } from "@tanstack/react-query";

import { cefrText, masteryBar } from "../lib/palette";
import { type CefrProgress } from "./CefrProgressCard";

type SkillRow = {
  id: number;
  code: string;
  cefrLevel?: string | null;
  nAttempts: number;
  retrievability: number;
  masteryProbability: number;
};

type VocabStats = {
  knownWords: number;
  wordsSeen: number;
  estimatedTextCoveragePercent: number;
};

// Today's activity + cumulative memory total — what the old standalone
// ProgressCard tile used to surface. The "skills above P 70%" count
// it also carried is intentionally dropped here: it conceptually
// overlaps with the CEFR "mastered + in progress" count below, just
// with a different threshold, and the two side-by-side read as
// redundant.
type DailyStats = {
  attemptsToday: number;
  correctToday: number;
  retentionDaysGainedToday: number;
  memoryTotal: number;
};

const BANDS = ["A1", "A2", "B1", "B2", "C1", "C2"] as const;
// Inter-band difficulty step — same as in CefrBandBreakdown. The
// prior for band N is centred at (adj[N-1] - STEP).
const STEP = 0.08;

export function LevelOverviewCard({
  language,
  skills,
  vocab,
  daily,
}: {
  language: string;
  skills: readonly SkillRow[];
  vocab: VocabStats | null;
  daily: DailyStats | null;
}) {
  const aggQ = useQuery({
    queryKey: ["cefr-progress", language],
    queryFn: async (): Promise<CefrProgress> => {
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || "/api"}/stats/cefr-progress?language=${encodeURIComponent(
          language,
        )}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`/stats/cefr-progress ${res.status}`);
      return (await res.json()) as CefrProgress;
    },
    staleTime: 60_000,
  });

  const stats = computeBandStats(skills);

  // Hide entirely until both data sources have arrived — a flash of
  // partial content reads as "you've made no progress" and hurts
  // morale.
  if (!aggQ.data || skills.length === 0) return null;
  const agg = aggQ.data;
  const confidence =
    agg.medianSigma <= 0.32
      ? "High"
      : agg.medianSigma <= 0.6
        ? "Calibrating"
        : "Just starting";

  return (
    <section className="mb-6 sm:mb-8 border border-ink-100 bg-paper">
      {/* Header strip: four small metric blocks on lg+ (wraps to
          2-col on phone, 3-col on tablet). The CEFR aggregate column
          was dropped — the band bars below already carry the same
          story at higher resolution, and the two together read as the
          same concept twice. Confidence is now a small numeric block,
          no more 3xl serif "Just starting" shouting at the user. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3 px-4 sm:px-5 py-3 sm:py-4 border-b border-ink-100">
        {daily ? (
          <div title="Today's practice — attempts and how much retention you gained from them.">
            <div className="eyebrow">Today</div>
            <div className="font-serif text-2xl sm:text-3xl text-accent2 tabular-nums leading-none mt-1">
              {daily.attemptsToday}
              <span className="text-ink-400 text-base"> tried</span>
            </div>
            <div className="text-xs text-ink-400 tabular-nums mt-1">
              {daily.correctToday}/{daily.attemptsToday} correct · +
              {daily.retentionDaysGainedToday.toFixed(1)}d retention
            </div>
          </div>
        ) : null}
        {daily ? (
          <div title="Sum of memory probabilities across every skill you've practiced — a rough 'how much do you remember right now'.">
            <div className="eyebrow">Memory total</div>
            <div className="font-serif text-2xl sm:text-3xl text-accent2 tabular-nums leading-none mt-1">
              {daily.memoryTotal.toFixed(1)}
            </div>
            <div className="text-xs text-ink-400 mt-1">across all skills</div>
          </div>
        ) : null}
        {vocab ? (
          <div
            title={`The headline % is the Zipfian-frequency estimate of how much of a typical day-to-day text you'd understand right now. The "known of seen" count below is the raw evidence — local accuracy on a curriculum-biased sample (you've seen the easier, more frequent words first, so it overstates real-world vocabulary; the headline % corrects for that by weighting words by how often they actually appear in text).`}
          >
            <div className="eyebrow">Vocabulary</div>
            <div className="font-serif text-2xl sm:text-3xl text-accent2 tabular-nums leading-none mt-1">
              ≈ {vocab.estimatedTextCoveragePercent.toFixed(0)}
              <span className="text-ink-400 text-base">%</span>
            </div>
            <div className="text-xs text-ink-400 tabular-nums mt-1">
              of typical text · {vocab.knownWords}/{vocab.wordsSeen} known
            </div>
          </div>
        ) : null}
        <div
          title={`How sharply the model has placed you on the CEFR axis. σ tightens as you accumulate per-skill evidence. Current: ${confidence}.`}
        >
          <div className="eyebrow">Confidence</div>
          <div className="font-serif text-2xl sm:text-3xl text-accent2 tabular-nums leading-none mt-1">
            σ {agg.medianSigma.toFixed(2)}
          </div>
          <div className="text-xs text-ink-400 mt-1">
            {confidence.toLowerCase()}
          </div>
        </div>
      </div>

      {/* Per-band rows — the visual centre of gravity. Compact: band
          code + summary on the left, calibrated memory + coverage on
          the right, two-layer bar below. */}
      <ul className="divide-y divide-ink-100">
        {stats.map((s) => {
          const rawPct = Math.round(s.rawP * 100);
          const adjPct = Math.round(s.adjP * 100);
          const coverPct = Math.round(s.coverage * 100);
          // Bars stay on the PRIMARY accent (opacity by mastery); the
          // secondary hue is reserved for the band letter + headline
          // numbers, so the two colors read as a deliberate pair.
          const fill =
            s.touched.length === 0 && coverPct === 0
              ? "bg-transparent"
              : masteryBar(s.adjP);
          const showRaw =
            s.touched.length > 0 && Math.abs(rawPct - adjPct) >= 2;
          return (
            <li
              key={s.band}
              className="px-4 sm:px-5 py-2.5"
              title={`${s.band}: ${s.touched.length} of ${s.total} skills practiced (${coverPct}% touched). Local avg memory ${rawPct}%; calibrated to ${adjPct}% by the lower-band prior.`}
            >
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span
                    className={`font-serif text-base font-semibold tabular-nums ${cefrText(s.band)}`}
                  >
                    {s.band}
                  </span>
                  <span className="text-xs text-ink-400">
                    {s.touched.length}/{s.total} practiced
                  </span>
                </div>
                <div className="text-xs text-ink-400 tabular-nums">
                  {s.touched.length === 0 && coverPct === 0 ? (
                    s.isCascade && stats[0] !== s ? (
                      <span title="No evidence in this band yet — calibrated estimate is the prior carried up from the lower band.">
                        prior {adjPct}%
                      </span>
                    ) : (
                      "—"
                    )
                  ) : (
                    <>
                      {adjPct}%
                      {showRaw && (
                        <span
                          className="text-ink-300"
                          title={`Raw local average across touched skills = ${rawPct}%. Calibrated to ${adjPct}%.`}
                        >
                          {" "}
                          (raw {rawPct}%)
                        </span>
                      )}{" "}
                      · {coverPct}% touched
                    </>
                  )}
                </div>
              </div>
              <div className="relative h-1.5 w-full bg-ink-100 overflow-hidden">
                <div
                  className="absolute inset-y-0 bg-ink-200"
                  style={{ width: `${coverPct}%` }}
                />
                <div
                  className={`absolute inset-y-0 ${fill}`}
                  style={{ width: `${Math.round(s.adjP * coverPct)}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────────

type BandStat = {
  band: string;
  total: number;
  touched: SkillRow[];
  coverage: number;
  rawP: number;
  adjP: number;
  isCascade: boolean;
};

function computeBandStats(skills: readonly SkillRow[]): BandStat[] {
  const byBand = new Map<string, SkillRow[]>();
  for (const s of skills) {
    const band = s.cefrLevel ?? "—";
    (byBand.get(band) ?? byBand.set(band, []).get(band)!).push(s);
  }
  const ordered = [
    ...BANDS.filter((b) => byBand.has(b)),
    ...[...byBand.keys()].filter(
      (b) => !BANDS.includes(b as (typeof BANDS)[number]),
    ),
  ];
  const stats: BandStat[] = [];
  let prevAdj: number | null = null;
  for (const band of ordered) {
    const bucket = byBand.get(band) ?? [];
    const total = bucket.length;
    const touched = bucket.filter((s) => s.nAttempts > 0);
    const coverage = total === 0 ? 0 : touched.length / total;
    // Use mastery probability (Bayesian "have I learned it?"), not
    // retrievability ("can I recall it right now?"). The retrievability
    // average sat near 1 immediately after practice and bore no
    // relation to actual skill level — that's what produced the
    // 97% B1 reading on 2026-06-04. The IRT belief, via
    // services/belief.mastery_probability, is what users actually mean.
    const rawP =
      touched.length === 0
        ? 0
        : touched.reduce((sum, s) => sum + s.masteryProbability, 0) /
          touched.length;
    const inCascade = BANDS.includes(band as (typeof BANDS)[number]);
    let adjP: number;
    if (!inCascade || prevAdj === null) {
      adjP = rawP;
    } else {
      const prior = Math.max(0, prevAdj - STEP);
      adjP = coverage === 0 ? prior : coverage * rawP + (1 - coverage) * prior;
    }
    if (inCascade) prevAdj = adjP;
    stats.push({
      band,
      total,
      touched,
      coverage,
      rawP,
      adjP,
      isCascade: inCascade,
    });
  }
  return stats;
}
